'use client';

import React, { useEffect } from 'react';
import { useSubscriptionNotificationsStore } from '@/features/subscription/model/subscription-notifications.store';
import { toast } from '@/shared/lib/toast';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';

const iconMap = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

export default function SubscriptionNotifications() {
  const notifications = useSubscriptionNotificationsStore(
    (state) => state.notifications
  );
  const dismissNotification = useSubscriptionNotificationsStore(
    (state) => state.dismissNotification
  );
  const [processedIds, setProcessedIds] = React.useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    notifications.forEach((notification) => {
      // Skip if already processed
      if (processedIds.has(notification.id)) return;

      const Icon = iconMap[notification.severity || notification.type];
      const toastId = notification.id;

      // Create custom toast content
      const toastContent = (
        <div className="flex items-start gap-3">
          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">{notification.title}</div>
            <div className="text-form-help mt-1">{notification.message}</div>
            {notification.action && (
              <button
                className="inline-block mt-2 text-form-label underline hover:no-underline"
                onClick={() => {
                  notification.action?.handler();
                  toast.dismiss(toastId);
                }}
              >
                {notification.action.label}
              </button>
            )}
          </div>
        </div>
      );

      // Show toast based on severity or type
      const notificationType = notification.severity || notification.type;
      switch (notificationType) {
        case 'success':
          toast.success(toastContent, {
            id: toastId,
            duration: 5000,
            onDismiss: () => dismissNotification(notification.id),
            onAutoClose: () => dismissNotification(notification.id),
          });
          break;
        case 'error':
          toast.error(toastContent, {
            id: toastId,
            duration: Infinity, // Don't auto-dismiss errors
            onDismiss: () => dismissNotification(notification.id),
          });
          break;
        case 'warning':
          toast.warning(toastContent, {
            id: toastId,
            duration: 10000,
            onDismiss: () => dismissNotification(notification.id),
            onAutoClose: () => dismissNotification(notification.id),
          });
          break;
        case 'info':
        default:
          toast.info(toastContent, {
            id: toastId,
            duration: 7000,
            onDismiss: () => dismissNotification(notification.id),
            onAutoClose: () => dismissNotification(notification.id),
          });
          break;
      }

      // Mark as processed
      setProcessedIds((prev) => new Set(prev).add(notification.id));
    });
  }, [notifications, dismissNotification, processedIds]);

  // Clean up processed IDs when notifications are removed
  useEffect(() => {
    const activeIds = new Set(notifications.map((n) => n.id));
    setProcessedIds((prev) => {
      const newSet = new Set<string>();
      prev.forEach((id) => {
        if (activeIds.has(id)) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  }, [notifications]);

  return null; // Sonner handles the rendering
}
